/**
 * As faturas selecionadas, um bloco por pagamento (AF-4139): "Recebimento de" e, abaixo, a tabela
 * das faturas daquele pagamento.
 *
 * Também mora aqui o que os dois diálogos (aprovar e reprovar) fazem com os blocos: uma chamada por
 * pagamento, e o resultado separado entre os que deram certo e os que falharam.
 */
import { PaymentRow } from '../services/billingApi';
import { PaymentGroup } from '../services/paymentsFilter';
import { formatBRL } from '../../../../utils/currencyUtils';

export const payerLabel = (payment: PaymentRow): string =>
    payment.payer?.name || `#${payment.paymentId.slice(0, 8)}`;

export interface PerPaymentOutcome {
    done: PaymentGroup[];
    failed: { group: PaymentGroup; message: string }[];
}

export const runPerPayment = async (
    groups: PaymentGroup[], call: (group: PaymentGroup) => Promise<unknown>,
): Promise<PerPaymentOutcome> => {
    const outcome: PerPaymentOutcome = { done: [], failed: [] };
    for (const group of groups) {
        try {
            await call(group);
            outcome.done.push(group);
        } catch (e: any) {
            outcome.failed.push({ group, message: e?.message || 'erro desconhecido' });
        }
    }
    return outcome;
};

const CELL = 'px-2 py-1';

const SelectedInvoicesSummary: React.FC<{ groups: PaymentGroup[] }> = ({ groups }) => {
    const invoices = groups.flatMap(group => group.invoices);
    const total = invoices.reduce((sum, inv) => sum + Number(inv.amountPaid || 0), 0);
    return (
        <div className="space-y-3 max-h-72 overflow-y-auto">
            {groups.map(({ payment, invoices: list }) => (
                <div key={payment.paymentId} className="space-y-1">
                    <p className="text-sm text-gray-700 dark:text-gray-200">
                        Recebimento de <strong>{formatBRL(payment.amount || 0)}</strong>
                        {payment.payer?.name && <> · {payment.payer.name}</>}
                    </p>
                    <table className="w-full text-sm text-gray-700 dark:text-gray-200">
                        <thead className="bg-gray-100 dark:bg-slate-700 text-xs text-gray-500 dark:text-gray-400 uppercase text-left">
                            <tr>
                                <th className={CELL}>Contrato</th>
                                <th className={CELL}>Operação</th>
                                <th className={CELL}>Título/NF</th>
                                <th className={`${CELL} text-right`}>Valor Pago</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(inv => (
                                <tr key={inv.invoiceId} className="border-t dark:border-slate-600">
                                    <td className={CELL}>{inv.contractLabel || '-'}</td>
                                    <td className={CELL}>{inv.operationNumber || '-'}</td>
                                    <td className={CELL}>{inv.details?.documentTitle || '-'}</td>
                                    <td className={`${CELL} text-right font-medium`}>
                                        {formatBRL(inv.amountPaid || inv.amount || 0)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            ))}
            {invoices.length > 1 && (
                <p className="text-sm text-gray-700 dark:text-gray-200">
                    Total: <strong>{formatBRL(total)}</strong>
                </p>
            )}
        </div>
    );
};

export default SelectedInvoicesSummary;
